import { useEffect, useMemo, useState } from 'react'

import { usePrevious } from '@mantine/hooks'
import { get, isEqual } from 'lodash-es'

import { SORT_FILTER_TRAIT_SORT_DIRECTION_DESC } from '../../constants'
import selectionColumn from '../../PaginatedTable/Columns/SelectionColumn'
import useControlledPagination from '../useControlledPagination'
import { isValidNumber, isValidString } from '../useDebouncedFilterValue/utils'
import usePaginatedQuery from '../usePaginatedQuery'
import usePaginatedTable from '../usePaginatedTable'
import useSortFilter from '../useSortFilter'
import {
  extractValidFilterFields,
  extractValidSorts,
} from './extract-valid-sort-filters'
import {
  UsePaginatedTableStack,
  UsePaginatedTableStackParams,
} from './use-paginated-table-stack'

const usePaginatedTableStack = <
  T extends Partial<Record<'id', string | number>>,
>({
  tableIdentifier,
  columns,
  queryUrl,
  initialPerPage = 50,
  initialSort = [
    { field: ['id'], direction: SORT_FILTER_TRAIT_SORT_DIRECTION_DESC },
  ],
  initialFilters = [],
  registeredFilterComponents = [],
  selectionState,
  queryOptions,
  customHeaders,
  noQueryParamSync,
  paginatedTableOptions,
  setExternalQueryKey,
  axiosInstance,
  setExternalRequestBody,
}: UsePaginatedTableStackParams<T>): UsePaginatedTableStack<T> => {
  const [allIdsOnActivePage, setAllIdsOnActivePage] = useState<
    (string | number)[]
  >([])

  const paginationState = useControlledPagination({
    total: 1,
    initialPerPage,
    noQueryParamSync,
    selectionState,
  })

  const { setTotal, perPage, setPage, active } = paginationState

  const columnsToUse = useMemo(() => {
    return typeof selectionState !== 'undefined'
      ? [
          selectionColumn<T>({
            selectedRows: selectionState?.selectedRows,
            setSelectedRows: selectionState?.setSelectedRows,
            allIdsOnActivePage: [...allIdsOnActivePage],
          }),
          ...columns,
        ]
      : columns
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columns,
    selectionState?.selectedRows,
    selectionState?.setSelectedRows,
    allIdsOnActivePage,
    selectionColumn,
  ])

  const sortFilter = useSortFilter({
    initialFilters,
    initialSort,
    localStorageFilterIdentifier: tableIdentifier,
    filterFieldsToFilterAgainst: extractValidFilterFields({
      registeredFilterComponents,
    }),
    sortKeysToFilterAgainst: extractValidSorts<T>({ columns: columnsToUse }),
    noQueryParamSync,
  })

  const { sort, activeFilters } = sortFilter

  const previousSortFilters = usePrevious(activeFilters)

  const paginatedQuery = usePaginatedQuery<T>({
    queryIdentifier: tableIdentifier,
    url: queryUrl,
    perPage,
    filters: activeFilters,
    sort,
    page: active,
    queryOptions,
    customHeaders,
    setExternalQueryKey,
    axios: axiosInstance,
    setExternalRequestBody,
  })

  const { tableInstance } = usePaginatedTable({
    paginatedQuery,
    columns: columnsToUse,
    paginatedTableOptions,
  })

  useEffect(() => {
    if (
      Boolean(previousSortFilters) &&
      !isEqual(previousSortFilters?.sort(), activeFilters.sort())
    ) {
      setPage(1)
    }
  }, [sortFilter.activeFilters])

  useEffect(() => {
    if (paginatedQuery.data?.meta?.total != null) {
      setTotal(paginatedQuery.data.meta.total)
    }
  }, [paginatedQuery.data?.meta?.total])

  useEffect(() => {
    if (!paginatedQuery.data?.data) return
    setAllIdsOnActivePage(
      (paginatedQuery.data?.data ?? [])
        .map((row) => {
          return get(row, 'id', '') as string | number
        })
        .sort()
        .filter(
          (id): id is string | number => isValidString(id) || isValidNumber(id),
        ),
    )
  }, [paginatedQuery.data?.data])

  return {
    paginatedQuery,
    paginationState,
    tableInstance,
    sortFilter,
    selectionState,
    allIdsOnActivePage,
    filterDefinitions: registeredFilterComponents,
  }
}
export default usePaginatedTableStack
